// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/birthdayReminders.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/birthdayReminders.js
//
// When to nudge someone about their own birthday, and what to say.
//
// ── Why the year is optional ───────────────────────────────────────────────
//
// None of this needs it. A reminder a week before someone's birthday needs a
// month and a day; the year only tells you how old they are. Asking for it
// makes the request feel like an ID check instead of a reminder, for no
// feature at all — so the flow asks for it last and lets it be skipped.
//
// The column is a date, so a birthday with no year is stored against a
// sentinel year and read back as month/day only. See parseBirthday.
//
// ── Why the schedule stops when they act ───────────────────────────────────
//
// Every one of these is a nudge to create a birthday invitation. Once they
// have created one, the nudges have done their job and become nagging about
// something already done — which is how people turn notifications off for
// good. The caller cancels the rest; this module says which are still due.

/** Stored against this year when the host did not give one. */
export const NO_YEAR = 1604;

/**
 * Month and day from a stored birthday, with the year only if it is real.
 *
 * @param {string|Date|null} value
 * @returns {{month: number, day: number, year: number|null}|null}
 *   `month` is 1-12, unlike a JS Date, because every caller here is reasoning
 *   about calendars rather than about Date arithmetic.
 */
export function parseBirthday(value) {
  if (!value) return null;

  // A 'YYYY-MM-DD' string is parsed by hand rather than by `new Date(...)`:
  // that constructor reads a bare date as UTC, so anyone west of Greenwich
  // gets the previous day. A birthday being a day early is exactly the bug
  // nobody notices until it happens to them.
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (!match) return null;
    const year = Number(match[1]);
    return {
      year: year === NO_YEAR ? null : year,
      month: Number(match[2]),
      day: Number(match[3]),
    };
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    return {
      year: year === NO_YEAR ? null : year,
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }

  return null;
}

/** 'YYYY-MM-DD' for storage, using the sentinel when no year was given. */
export function formatBirthday({ year, month, day }) {
  const y = String(year ?? NO_YEAR).padStart(4, '0');
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The next time this birthday comes round, at 10am local.
 *
 * 10am rather than midnight: a notification that arrives at 00:00 is read the
 * next morning at best and resented at worst.
 *
 * 29 February falls back to 1 March in common years — the alternative is
 * skipping three years in four.
 */
export function nextOccurrence({ month, day }, now = new Date()) {
  const at = (year) => {
    const date = new Date(year, month - 1, day, 10, 0, 0, 0);
    // A 29 Feb in a common year rolls into 1 March, which is what we want,
    // but JS gets there by overflowing — so check it landed in the right
    // month and correct it if not.
    if (date.getMonth() !== month - 1) return new Date(year, month - 1 + 1, 1, 10, 0, 0, 0);
    return date;
  };

  const thisYear = at(now.getFullYear());
  return thisYear > now ? thisYear : at(now.getFullYear() + 1);
}

/**
 * The nudges, in order.
 *
 * `daysBefore` is how far ahead of the birthday each one fires. The copy
 * escalates: a week out is "you have time to plan", the day itself is a wish
 * that happens to mention it is not too late.
 */
export const REMINDERS = [
  {
    id: 'week',
    daysBefore: 7,
    title: 'Your birthday is a week away 🎂',
    body: 'Enough time to get everyone in one place. Start an invite and see who is in.',
  },
  {
    id: 'two-days',
    daysBefore: 2,
    title: 'Two days to your birthday 🎉',
    body: 'A house party, dinner, or something bigger? Send an invite — people will come.',
  },
  {
    id: 'day-before',
    daysBefore: 1,
    title: 'Birthday tomorrow 🥳',
    body: 'Still time for a last-minute bash. One invite, and your people know where to be.',
  },
  {
    id: 'day-of',
    daysBefore: 0,
    title: 'Happy birthday! 🎈',
    body: 'Have a brilliant one. If you are celebrating tonight, an invite takes a minute.',
  },
];

/**
 * The reminders still worth scheduling.
 *
 * Anything already past is dropped — scheduling it would either fire straight
 * away or be rejected by the OS, and "your birthday is a week away" arriving
 * the day after is worse than silence.
 *
 * @param {{month: number, day: number}} birthday
 * @param {Date} [now]
 * @returns {Array<{id: string, at: Date, title: string, body: string}>}
 */
export function dueReminders(birthday, now = new Date()) {
  if (!birthday) return [];

  const birthdayAt = nextOccurrence(birthday, now);

  return REMINDERS
    .map((reminder) => {
      const at = new Date(birthdayAt);
      at.setDate(at.getDate() - reminder.daysBefore);
      return { id: reminder.id, at, title: reminder.title, body: reminder.body };
    })
    .filter((reminder) => reminder.at > now);
}

/**
 * '2026-10-05' as LOCAL midnight of that calendar day. `new Date()` reads a
 * bare date as UTC midnight - the evening before in the Americas - so US
 * users saw events a day early, and an event today counted as past. (Kept
 * here rather than imported from eventDateTime.js: shared modules stay
 * import-free so their tests can load them.)
 */
function localDate(value) {
  const m = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
}

/**
 * Has this person already made an invitation for their own birthday?
 *
 * Read loosely on purpose. A host who has made ANY invitation landing within
 * a few days of their birthday has done the thing these notifications exist
 * to prompt, whatever they called it — telling them to "plan something" when
 * the plan is already made is the fastest way to get notifications turned
 * off. Better to go quiet on a maybe than to nag on a certainty.
 */
export function hasBirthdayInvitation(birthday, events = [], now = new Date()) {
  if (!birthday || !Array.isArray(events)) return false;

  const birthdayAt = nextOccurrence(birthday, now);
  const windowMs = 3 * 24 * 60 * 60 * 1000;

  return events.some((event) => {
    const when = event?.event_date || event?.start_datetime;
    if (!when) return false;
    const date = localDate(when);
    if (Number.isNaN(date.getTime())) return false;
    return Math.abs(date.getTime() - birthdayAt.getTime()) <= windowMs;
  });
}
